import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useBrand } from "@/contexts/brand-context";
import { useSubscription } from "@/contexts/subscription-context";
import { useAuth } from "@/contexts/auth-context";

/**
 * Centralized navigation flow hook that handles routing decisions
 * based on brand and subscription state
 */
export const useNavigationFlow = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { brands, isLoading: isBrandsLoading } = useBrand();
  const { isAuthenticated } = useAuth();
  const {
    isLoading: isSubscriptionLoading,
    shouldStartTrial,
    subscriptionExpired,
  } = useSubscription();

  useEffect(() => {
    if (!isAuthenticated) {
      console.log("Redirecting to sign in page");
      navigate("/signin");
      return;
    }

    if (isBrandsLoading || isSubscriptionLoading) return;

    // Skip navigation for certain routes that don't require this logic
    const skipRoutes = [
      "/signin",
      "/signup",
      "/embed-signup",
      "/forgot-password",
      "/404",
      "/start-trial",
      "/plans",
    ];
    if (skipRoutes.some((route) => location.pathname.startsWith(route))) {
      return;
    }

    if (shouldStartTrial) {
      console.log("Redirecting to trial subscription paywall");
      navigate("/start-trial");
      return;
    }

    if (subscriptionExpired) {
      console.log("Redirecting to subscription page");
      navigate("/plans");
      return;
    }

    if (brands.length === 0) {
      console.log("Redirecting to onboarding");
      navigate("/onboarding");
    }
  }, [
    isAuthenticated,
    brands.length,
    isBrandsLoading,
    subscriptionExpired,
    isSubscriptionLoading,
    shouldStartTrial,
    location.pathname,
    navigate,
  ]);

  const isLoading = isBrandsLoading || isSubscriptionLoading;

  return {
    isLoading,
    shouldShowContent: !isLoading,
  };
};
